import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorefrontIcon from '@mui/icons-material/Storefront';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { LOCAL_PARTNER_TYPES, RADIUS_OPTIONS, typeLabel } from '../constants';

const LOGO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
const LOGO_MAX_BYTES = 5 * 1024 * 1024;

export default function ShopProfilePage() {
  const { vendor, refreshShop } = useAuth();
  const [form, setForm] = useState({
    restaurant_name: '',
    phone_e164: '',
    whatsapp_number: '',
    address: '',
    open_time: '09:00',
    close_time: '21:00',
    delivery_radius_km: 0.5,
    vendor_type: 'other',
  });
  const [busy, setBusy] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pendingLogo, setPendingLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!vendor) return;
    const vt = String(vendor.vendor_type || '')
      .trim()
      .toLowerCase();
    setForm({
      restaurant_name: vendor.name || '',
      phone_e164: vendor.phone_e164 || '',
      whatsapp_number: vendor.whatsapp_number || vendor.phone_e164 || '',
      address: vendor.address || '',
      open_time: vendor.open_time || '09:00',
      close_time: vendor.close_time || '21:00',
      delivery_radius_km: vendor.delivery_radius_km ?? 0.5,
      vendor_type: LOCAL_PARTNER_TYPES.has(vt) ? vt : 'other',
    });
    if (!pendingLogo) {
      setLogoPreview(vendor.logo_url || null);
    }
  }, [vendor, pendingLogo]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onPickLogo = (file) => {
    if (!file) return;
    const allowed = LOGO_ACCEPT.split(',');
    if (!allowed.includes(file.type)) {
      setError('Please choose a JPEG, PNG, WebP, or GIF image');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setError('Logo must be 5MB or smaller');
      return;
    }
    setError('');
    setSuccess('');
    setPendingLogo(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target?.result || null);
    reader.readAsDataURL(file);
  };

  const uploadLogo = async () => {
    if (!pendingLogo) {
      setError('Choose a logo image first');
      return;
    }
    setError('');
    setSuccess('');
    setUploadingLogo(true);
    try {
      const body = new FormData();
      body.append('file', pendingLogo);
      await api.post('/api/vendor/upload-logo', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPendingLogo(null);
      await refreshShop();
      setSuccess('Shop logo uploaded');
    } catch (err) {
      setError(formatApiError(err, 'Could not upload logo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const save = async () => {
    setError('');
    setSuccess('');
    if (!form.restaurant_name.trim()) {
      setError('Shop name is required');
      return;
    }
    setBusy(true);
    try {
      await api.put('/vendor/auth/settings', {
        restaurant_name: form.restaurant_name.trim(),
        phone_e164: form.phone_e164.trim() || null,
        whatsapp_number: form.whatsapp_number.trim() || form.phone_e164.trim() || null,
        address: form.address.trim() || null,
        open_time: form.open_time,
        close_time: form.close_time,
        delivery_radius_km: Number(form.delivery_radius_km) || 0.5,
        // Shop type is fixed at signup — do not send vendor_type on save.
      });
      await refreshShop();
      setSuccess('Shop profile saved');
    } catch (err) {
      setError(formatApiError(err, 'Could not save profile'));
    } finally {
      setBusy(false);
    }
  };

  const displayLogo = logoPreview || vendor?.logo_url || null;

  return (
    <Box>
      <Typography variant="h5" fontWeight={900} sx={{ mb: 0.5 }}>
        Shop profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        WhatsApp, hours, and delivery radius guests see for your shop.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={typeLabel(form.vendor_type || vendor?.vendor_type)}
              sx={{ fontWeight: 800, bgcolor: '#ccfbf1', color: '#0f766e' }}
            />
            <Typography variant="caption" color="text.secondary">
              {vendor?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {vendor?.country_code} · {vendor?.bank_currency}
            </Typography>
          </Stack>

          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
              Shop type
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Set when your shop was created. Neighbour category (Grocery / Pharmacy / Laundry /
              More) comes from this type — contact support if it needs changing.
            </Typography>
            <Chip
              label={typeLabel(form.vendor_type || vendor?.vendor_type)}
              color="primary"
              sx={{ fontWeight: 800 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
              Shop logo
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Avatar
                src={displayLogo || undefined}
                alt={vendor?.name || 'Shop logo'}
                variant="rounded"
                sx={{
                  width: 88,
                  height: 88,
                  bgcolor: '#ccfbf1',
                  color: '#0f766e',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontSize: 36,
                }}
              >
                <StorefrontIcon fontSize="inherit" />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  JPEG, PNG, WebP, or GIF · max 5MB. Shown to guests on your shop.
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={busy || uploadingLogo}
                  >
                    {pendingLogo || displayLogo ? 'Change logo' : 'Choose logo'}
                    <input
                      type="file"
                      accept={LOGO_ACCEPT}
                      hidden
                      onChange={(e) => {
                        onPickLogo(e.target.files?.[0] || null);
                        e.target.value = '';
                      }}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={
                      uploadingLogo ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <CloudUploadIcon />
                      )
                    }
                    onClick={uploadLogo}
                    disabled={!pendingLogo || busy || uploadingLogo}
                  >
                    Upload logo
                  </Button>
                </Stack>
                {pendingLogo && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    Ready to upload: {pendingLogo.name}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          <TextField
            label="Shop name"
            value={form.restaurant_name}
            onChange={(e) => setField('restaurant_name', e.target.value)}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Phone"
              value={form.phone_e164}
              onChange={(e) => setField('phone_e164', e.target.value)}
              fullWidth
              placeholder="+971…"
            />
            <TextField
              label="WhatsApp"
              value={form.whatsapp_number}
              onChange={(e) => setField('whatsapp_number', e.target.value)}
              fullWidth
              placeholder="+971…"
              helperText="Guests message this number"
            />
          </Stack>
          <TextField
            label="Address"
            value={form.address}
            onChange={(e) => setField('address', e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="Opens"
              type="time"
              value={form.open_time}
              onChange={(e) => setField('open_time', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Closes"
              type="time"
              value={form.close_time}
              onChange={(e) => setField('close_time', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Box>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>
              Delivery radius
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Residents/buildings within this radius see your shop on GoSnel Neighbour.
            </Typography>
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {RADIUS_OPTIONS.map((km) => (
                <Chip
                  key={km}
                  label={`${km} km`}
                  color={Number(form.delivery_radius_km) === km ? 'primary' : 'default'}
                  variant={Number(form.delivery_radius_km) === km ? 'filled' : 'outlined'}
                  onClick={() => setField('delivery_radius_km', km)}
                  sx={{ fontWeight: 800 }}
                />
              ))}
            </Stack>
          </Box>

          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={save}
            disabled={busy}
            sx={{ alignSelf: 'flex-start', py: 1.2, px: 2.5 }}
          >
            Save profile
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
